import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import PanelValidacion from '../components/common/PanelValidacion';
import GaleriaFotos from '../components/common/GaleriaFotos';
import { sstService, sesionActualSST, descargarReporteSST, descargarReporteExcelSST, descargarReporteExcelInspectoriaSST, descargarPlantillaChecklistSST } from '../services/sst';
import toast from 'react-hot-toast';
import {
    ShieldCheck,
    ChevronDown,
    ChevronUp,
    CheckCircle2,
    XCircle,
    MinusCircle,
    Download,
    AlertTriangle,
    Minus,
    History,
    Building2,
    Upload,
    FileSpreadsheet,
    RotateCcw,
    Globe,
} from 'lucide-react';

const CT_STORAGE_KEY = 'sst_ct_seleccionado';

// ─── Constantes ───────────────────────────────────────────────────────────────

const OPCIONES_SI_NO_NA = [
    { value: 'si',  label: 'SI',  icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-500', activeBg: 'bg-green-50 border-green-400' },
    { value: 'no',  label: 'NO',  icon: XCircle,      color: 'text-red-600',   bg: 'bg-red-500',   activeBg: 'bg-red-50 border-red-400' },
    { value: 'na',  label: 'N/A', icon: MinusCircle,  color: 'text-gray-500',  bg: 'bg-gray-400',  activeBg: 'bg-gray-50 border-gray-400' },
];

const OPCIONES_ESTADO = [
    { value: 'buen_estado',     label: 'BUEN ESTADO', icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-500', activeBg: 'bg-green-50 border-green-400' },
    { value: 'regular_estado',  label: 'REGULAR',     icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-500', activeBg: 'bg-yellow-50 border-yellow-400' },
    { value: 'mal_estado',      label: 'MAL ESTADO',  icon: XCircle,      color: 'text-red-600',   bg: 'bg-red-500',   activeBg: 'bg-red-50 border-red-400' },
];

const RESP_BG = {
    si: 'bg-green-50',
    buen_estado: 'bg-green-50',
    no: 'bg-red-50',
    mal_estado: 'bg-red-50',
    na: 'bg-gray-50',
    regular_estado: 'bg-yellow-50',
};

// ─── Item de inspección ───────────────────────────────────────────────────────

const ItemSST = ({ item, tipoRespuesta, sesion, ct, onActualizar, esSesionHistorica = false }) => {
    const [obs, setObs] = useState(item.observaciones || '');
    const [guardandoObs, setGuardandoObs] = useState(false);
    const opciones = tipoRespuesta === 'estado' ? OPCIONES_ESTADO : OPCIONES_SI_NO_NA;

    const handleRespuesta = async (valor) => {
        if (!ct) { toast.error('Selecciona un CT primero'); return; }
        try {
            const resp = await sstService.responder(item.id, valor, obs || null, sesion, ct);
            onActualizar(item.id, valor, obs || null);
            // Al marcar "NO" el backend genera una Solicitud de trabajo (hallazgo SST)
            if (valor === 'no' && resp?.solicitud) {
                if (resp.solicitud.ya_existia) {
                    toast(`Solicitud ya generada: ${resp.solicitud.codigo}`, { icon: '📋' });
                } else {
                    toast.success(`Solicitud generada: ${resp.solicitud.codigo}`, { icon: '📋' });
                }
            }
            if (resp?.solicitud_error) {
                toast.error(resp.solicitud_error);
            }
        } catch {
            toast.error('Error al guardar');
        }
    };

    const handleGuardarObs = async () => {
        if (!ct) { toast.error('Selecciona un CT primero'); return; }
        if (!item.respuesta) { toast.error('Selecciona una respuesta primero'); return; }
        setGuardandoObs(true);
        try {
            await sstService.responder(item.id, item.respuesta, obs || null, sesion, ct);
            onActualizar(item.id, item.respuesta, obs || null);
            toast.success('Observación guardada');
        } catch {
            toast.error('Error al guardar');
        } finally {
            setGuardandoObs(false);
        }
    };

    const rowBg = item.respuesta ? RESP_BG[item.respuesta] || '' : '';

    return (
        <div className={`border border-gray-100 rounded-lg p-3 mb-2 transition-colors ${rowBg}`}>
            <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                {/* Número + texto */}
                <div className="flex-1 min-w-0">
                    <span className="text-xs font-bold text-gray-400 mr-2">{item.numero}</span>
                    <span className="text-sm text-gray-800">{item.texto}</span>
                </div>

                {/* Botones de respuesta */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    {opciones.map((op) => {
                        const Icon = op.icon;
                        const activo = item.respuesta === op.value;
                        return (
                            <button
                                key={op.value}
                                onClick={() => handleRespuesta(op.value)}
                                disabled={esSesionHistorica}
                                className={`flex items-center px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                    activo
                                        ? `${op.activeBg} ${op.color} border-2`
                                        : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                                }`}
                            >
                                <Icon className={`h-3.5 w-3.5 mr-1 ${activo ? op.color : 'text-gray-400'}`} />
                                {op.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Observaciones */}
            <div className="mt-2 flex gap-2">
                <input
                    type="text"
                    value={obs}
                    onChange={(e) => setObs(e.target.value)}
                    placeholder="Observaciones (opcional)..."
                    readOnly={esSesionHistorica}
                    className={`flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white ${
                        esSesionHistorica ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                    }`}
                    onKeyDown={(e) => e.key === 'Enter' && handleGuardarObs()}
                />
                {!esSesionHistorica && obs !== (item.observaciones || '') && (
                    <button
                        onClick={handleGuardarObs}
                        disabled={guardandoObs}
                        className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-60"
                    >
                        {guardandoObs ? '...' : 'Guardar'}
                    </button>
                )}
            </div>

            <GaleriaFotos modulo="sst_generales" itemId={item.id} sesion={sesion} ct={ct} soloLectura={esSesionHistorica} />
        </div>
    );
};

// ─── Acordeón de sección ──────────────────────────────────────────────────────

const SeccionAccordion = ({ seccion, sesion, ct, onActualizar, esSesionHistorica = false }) => {
    const [abierto, setAbierto] = useState(false);
    const pct = seccion.total > 0 ? Math.round((seccion.respondidos / seccion.total) * 100) : 0;
    const completo = seccion.respondidos === seccion.total && seccion.total > 0;

    return (
        <div className={`border rounded-xl overflow-hidden mb-3 ${completo ? 'border-green-300' : 'border-gray-200'}`}>
            {/* Cabecera */}
            <button
                onClick={() => setAbierto(!abierto)}
                className={`w-full flex items-center justify-between px-5 py-4 transition-colors ${
                    completo ? 'bg-green-50 hover:bg-green-100' : 'bg-white hover:bg-gray-50'
                }`}
            >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className={`text-sm font-bold px-2 py-0.5 rounded-full ${
                        completo ? 'bg-green-200 text-green-800' : 'bg-blue-100 text-blue-700'
                    }`}>
                        {seccion.numero}
                    </span>
                    <span className="font-semibold text-gray-800 text-sm truncate">{seccion.titulo}</span>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    {/* Mini barra de progreso */}
                    <div className="hidden sm:flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-1.5">
                            <div
                                className={`h-1.5 rounded-full transition-all ${completo ? 'bg-green-500' : 'bg-blue-500'}`}
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                            {seccion.respondidos}/{seccion.total}
                        </span>
                    </div>
                    {completo && <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />}
                    {abierto ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                </div>
            </button>

            {/* Subtítulo de columnas + ítems */}
            {abierto && (
                <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-4">
                    {/* Etiqueta de tipo */}
                    <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Cumplimiento:
                        </span>
                        {seccion.tipo_respuesta === 'estado'
                            ? <><span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">BUEN ESTADO</span>
                               <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full font-medium">REGULAR</span>
                               <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">MAL ESTADO</span></>
                            : <><span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">SI</span>
                               <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">NO</span>
                               <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium">N/A</span></>
                        }
                    </div>

                    {seccion.items.map((item) => (
                        <ItemSST
                            key={item.id}
                            item={item}
                            tipoRespuesta={seccion.tipo_respuesta}
                            sesion={sesion}
                            ct={ct}
                            onActualizar={(id, respuesta, observaciones) =>
                                onActualizar(seccion.id, id, respuesta, observaciones)
                            }
                            esSesionHistorica={esSesionHistorica}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Página principal ─────────────────────────────────────────────────────────

const SSTPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [secciones, setSecciones] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [descargando, setDescargando] = useState(false);
    const [descargandoExcel, setDescargandoExcel] = useState(false);
    const [descargandoExcelInspectoria, setDescargandoExcelInspectoria] = useState(false);
    const [cts, setCts] = useState([]);
    const [subiendoExcel, setSubiendoExcel] = useState(false);
    const excelInputRef = useRef(null);

    const sesionParam = searchParams.get('sesion');
    const ctParam = searchParams.get('ct');
    const sesion = sesionParam || sesionActualSST();
    const ct = ctParam || (typeof window !== 'undefined' ? localStorage.getItem(CT_STORAGE_KEY) : '') || '';
    const esSesionHistorica = !!sesionParam && sesionParam !== sesionActualSST();

    const setCt = (nuevoCt) => {
        const next = new URLSearchParams(searchParams);
        if (nuevoCt) {
            next.set('ct', nuevoCt);
            try { localStorage.setItem(CT_STORAGE_KEY, nuevoCt); } catch {}
        } else {
            next.delete('ct');
            try { localStorage.removeItem(CT_STORAGE_KEY); } catch {}
        }
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        (async () => {
            try {
                const lista = await sstService.listarCts();
                setCts(lista);
            } catch {
                toast.error('No se pudo cargar la lista de CTs');
            }
        })();
    }, []);

    const cargar = async () => {
        if (!ct) { setSecciones([]); return; }
        setCargando(true);
        try {
            const data = await sstService.checklist(sesion, ct);
            setSecciones(data);
        } catch {
            toast.error('Error al cargar el checklist SST');
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => { cargar(); }, [sesion, ct]);

    const handleActualizar = useCallback((seccionId, itemId, respuesta, observaciones) => {
        setSecciones((prev) =>
            prev.map((sec) => {
                if (sec.id !== seccionId) return sec;
                const nuevosItems = sec.items.map((it) =>
                    it.id === itemId ? { ...it, respuesta, observaciones } : it
                );
                const respondidos = nuevosItems.filter((it) => it.respuesta).length;
                return { ...sec, items: nuevosItems, respondidos };
            })
        );
    }, []);

    const handleDescargar = async () => {
        if (!ct) { toast.error('Selecciona un CT primero'); return; }
        setDescargando(true);
        try {
            await descargarReporteSST(sesion, ct);
            toast.success('Reporte SST descargado');
        } catch {
            toast.error('Error al descargar el reporte');
        } finally {
            setDescargando(false);
        }
    };

    const handleDescargarExcel = async () => {
        if (!ct) { toast.error('Selecciona un CT primero'); return; }
        setDescargandoExcel(true);
        try {
            await descargarReporteExcelSST(sesion, ct);
            toast.success('Excel SST descargado');
        } catch {
            toast.error('Error al descargar el Excel');
        } finally {
            setDescargandoExcel(false);
        }
    };

    const handleDescargarExcelInspectoria = async () => {
        setDescargandoExcelInspectoria(true);
        try {
            await descargarReporteExcelInspectoriaSST(sesion);
            toast.success('Excel de toda la inspectoria descargado');
        } catch {
            toast.error('Error al descargar el Excel de la inspectoria');
        } finally {
            setDescargandoExcelInspectoria(false);
        }
    };

    const handleDescargarPlantilla = async () => {
        try {
            await descargarPlantillaChecklistSST();
        } catch {
            toast.error('Error al descargar la plantilla');
        }
    };

    const handleSubirExcel = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setSubiendoExcel(true);
        try {
            const resultado = await sstService.importarChecklistExcel(file);
            toast.success(resultado.message || 'Checklist actualizado');
            await cargar();
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Error al cargar el Excel');
        } finally {
            setSubiendoExcel(false);
            if (excelInputRef.current) excelInputRef.current.value = '';
        }
    };

    const handleRestaurarDefault = async () => {
        if (!window.confirm('¿Restaurar el checklist por defecto (SST_PR_07 – RE08) para todos los almacenes?')) return;
        try {
            const resultado = await sstService.restaurarChecklistDefault();
            toast.success(resultado.message || 'Checklist restaurado');
            await cargar();
        } catch {
            toast.error('Error al restaurar el checklist');
        }
    };

    const totalItems = secciones.reduce((s, sec) => s + sec.total, 0);
    const totalResp = secciones.reduce((s, sec) => s + sec.respondidos, 0);
    const pctGlobal = totalItems > 0 ? Math.round((totalResp / totalItems) * 100) : 0;

    // Conteos globales de respuestas
    const conteos = { si: 0, no: 0, na: 0, buen_estado: 0, regular_estado: 0, mal_estado: 0 };
    secciones.forEach((sec) => sec.items.forEach((it) => { if (it.respuesta) conteos[it.respuesta] = (conteos[it.respuesta] || 0) + 1; }));

    return (
        <Layout>
            <div className="p-6">
                {/* Header */}
                <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <ShieldCheck className="h-7 w-7 text-orange-600" />
                            Inspección de Seguridad y Salud
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">
                            Sesión: <span className="font-semibold text-orange-600">{sesion}</span>
                            {ct && <> · CT: <span className="font-semibold text-orange-600">{ct}</span></>}
                            {' · '}SST_PR_07 – RE08
                        </p>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            onClick={() => navigate('/inspeccion-sst/historial')}
                            className="flex items-center px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold text-sm shadow-sm"
                        >
                            <History className="h-4 w-4 mr-2" />
                            Historial
                        </button>
                        <button
                            onClick={handleDescargar}
                            disabled={descargando}
                            className="flex items-center px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-60 transition-colors font-semibold text-sm"
                        >
                            {descargando
                                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                : <Download className="h-4 w-4 mr-2" />}
                            {descargando ? 'Descargando...' : 'Descargar PDF'}
                        </button>
                        <button
                            onClick={handleDescargarExcel}
                            disabled={descargandoExcel}
                            className="flex items-center px-4 py-2.5 bg-emerald-700 text-white rounded-xl hover:bg-emerald-800 disabled:opacity-60 transition-colors font-semibold text-sm"
                        >
                            {descargandoExcel
                                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                : <FileSpreadsheet className="h-4 w-4 mr-2" />}
                            {descargandoExcel ? 'Descargando...' : 'Descargar Excel'}
                        </button>
                        <button
                            onClick={handleDescargarExcelInspectoria}
                            disabled={descargandoExcelInspectoria}
                            title="Excel consolidado con todos los Centros de Trabajo de la inspectoria"
                            className="flex items-center px-4 py-2.5 bg-slate-700 text-white rounded-xl hover:bg-slate-800 disabled:opacity-60 transition-colors font-semibold text-sm"
                        >
                            {descargandoExcelInspectoria
                                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                : <Globe className="h-4 w-4 mr-2" />}
                            {descargandoExcelInspectoria ? 'Descargando...' : 'Excel toda la inspectoria'}
                        </button>
                    </div>
                </div>

                {/* Selector de CT */}
                <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2 text-gray-700">
                            <Building2 className="h-5 w-5 text-orange-600" />
                            <span className="text-sm font-semibold">Centro de Trabajo:</span>
                        </div>
                        <select
                            value={ct}
                            onChange={(e) => setCt(e.target.value)}
                            className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                            <option value="">— Selecciona un CT —</option>
                            {cts.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                        {ct && (
                            <span className="text-xs text-gray-500">
                                Inspección independiente por CT y quincena
                            </span>
                        )}
                    </div>

                    {/* Carga de checklist global (un solo Excel para todos los almacenes) */}
                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">
                            Checklist general (todos los almacenes):
                        </span>
                        <button
                            onClick={handleDescargarPlantilla}
                            className="flex items-center px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs font-medium"
                            title="Descargar plantilla Excel"
                        >
                            <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
                            Descargar plantilla
                        </button>
                        <button
                            onClick={() => excelInputRef.current?.click()}
                            disabled={subiendoExcel}
                            className="flex items-center px-3 py-1.5 bg-orange-50 border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-100 disabled:opacity-60 transition-colors text-xs font-medium"
                            title="Subir checklist Excel general"
                        >
                            {subiendoExcel
                                ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-orange-700 mr-1.5" />
                                : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                            {subiendoExcel ? 'Cargando...' : 'Subir Excel'}
                        </button>
                        <input
                            ref={excelInputRef}
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleSubirExcel}
                            className="hidden"
                        />
                        <button
                            onClick={handleRestaurarDefault}
                            className="flex items-center px-3 py-1.5 bg-white border border-gray-300 text-gray-500 rounded-lg hover:bg-gray-50 transition-colors text-xs font-medium"
                            title="Restaurar checklist por defecto"
                        >
                            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                            Restaurar checklist por defecto
                        </button>
                    </div>
                </div>

                {ct && <PanelValidacion modulo="sst_generales" sesion={sesion} ct={ct} />}

                {/* Banner sesión histórica */}
                {esSesionHistorica && (
                    <div className="mb-4 flex items-center justify-between gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <div className="flex items-center gap-2 text-amber-800 text-sm">
                            <History className="h-4 w-4" />
                            <span>Estás viendo una sesión <strong>histórica</strong>. Las respuestas no se modifican aquí.</span>
                        </div>
                        <button
                            onClick={() => navigate('/inspeccion-sst')}
                            className="text-amber-800 text-xs font-semibold underline hover:no-underline"
                        >
                            Ir a la sesión actual
                        </button>
                    </div>
                )}

                {/* Resumen global */}
                {totalItems > 0 && (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                                <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto mb-1" />
                                <p className="text-xl font-bold text-green-700">{conteos.si + conteos.buen_estado}</p>
                                <p className="text-xs text-green-600">SI / Buen estado</p>
                            </div>
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                                <XCircle className="h-5 w-5 text-red-500 mx-auto mb-1" />
                                <p className="text-xl font-bold text-red-700">{conteos.no + conteos.mal_estado}</p>
                                <p className="text-xs text-red-600">NO / Mal estado</p>
                            </div>
                            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                                <AlertTriangle className="h-5 w-5 text-yellow-500 mx-auto mb-1" />
                                <p className="text-xl font-bold text-yellow-700">{conteos.regular_estado}</p>
                                <p className="text-xs text-yellow-600">Regular estado</p>
                            </div>
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                                <Minus className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                                <p className="text-xl font-bold text-gray-600">{totalItems - totalResp}</p>
                                <p className="text-xs text-gray-500">Pendientes</p>
                            </div>
                        </div>

                        {/* Barra de progreso global */}
                        <div className="mb-6">
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                                <span>Progreso general</span>
                                <span>{totalResp}/{totalItems} respondidos — {pctGlobal}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div
                                    className="bg-orange-500 h-2.5 rounded-full transition-all duration-500"
                                    style={{ width: `${pctGlobal}%` }}
                                />
                            </div>
                        </div>
                    </>
                )}

                {/* Checklist por secciones */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <h2 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5 text-orange-500" />
                        Aspectos a Verificar — {secciones.length} secciones
                    </h2>

                    {!ct && (
                        <div className="text-center py-14 text-gray-400">
                            <Building2 className="h-14 w-14 mx-auto mb-3 opacity-20" />
                            <p className="text-sm font-medium">Selecciona un Centro de Trabajo para iniciar la inspección.</p>
                        </div>
                    )}

                    {ct && cargando && (
                        <div className="flex justify-center py-10">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
                        </div>
                    )}

                    {ct && !cargando && secciones.length === 0 && (
                        <div className="text-center py-14 text-gray-400">
                            <ShieldCheck className="h-14 w-14 mx-auto mb-3 opacity-20" />
                            <p className="text-sm">No se encontraron secciones.</p>
                        </div>
                    )}

                    {ct && !cargando && secciones.map((sec) => (
                        <SeccionAccordion
                            key={sec.id}
                            seccion={sec}
                            sesion={sesion}
                            ct={ct}
                            onActualizar={handleActualizar}
                            esSesionHistorica={esSesionHistorica}
                        />
                    ))}
                </div>
            </div>

        </Layout>
    );
};

export default SSTPage;
